import { Plus, FileWarning } from "lucide-react";
import { useLocation } from "wouter";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import { ProjectResourceHeader } from "@/components/gestao-recursos/project-resource-header";
import { ResourceBudgetCard } from "@/components/gestao-recursos/resource-budget-card";
import { MonthlyStatsBarChart } from "@/components/gestao-recursos/monthly-stats-bar-chart";
import { ResourcesByLawChart } from "@/components/gestao-recursos/resources-by-law-chart";
import { useProjectResources } from "@/services/queries/useRecursos";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectResources } from "@/interfaces/project.interface";
import { useEffect, useState } from "react";
import { useProjetoContext } from "@/context/projeto";

export default function GestaoRecursos() {
  const [, setLocation] = useLocation();
  const [apiData, setApiData] = useState<ProjectResources | null>(null);

  // Get selected project from context
  const { projetoSelecionado } = useProjetoContext();
  const idProjeto = projetoSelecionado?.id?.toString() ?? "";

  // Fetch project resources from API
  const { data: resourcesData, isLoading, isError } = useProjectResources(idProjeto);

  useEffect(() => {
    if (resourcesData) {
      setApiData(resourcesData?.data);
    }
  }, [resourcesData]);

  const hasData = apiData && apiData.budgetSummary && apiData.budgetSummary.orcamentoTotal > 0;

  // Handlers
  const handleGoToProject = () => {
    setLocation("/cadastrar-projeto");
  };

  const handleEditResources = () => {
    setLocation("/financeiro/gestao-recursos/editar");
  };

  // Show message if no project is selected
  if (!idProjeto) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Texto de Apoio da página"
      >
        <div className="flex items-center justify-center h-64 text-gray-400">
          Selecione um projeto para visualizar os recursos
        </div>
      </AppContainer>
    );
  }

  // Show loading skeleton while data is being fetched
  if (isLoading) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Texto de Apoio da página"
      >
        <div className="flex gap-6">
          <div className="flex flex-col gap-[30px] w-[892px]">
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[150px] w-full rounded-lg" />
            <Skeleton className="h-[80px] w-full rounded-lg" />
            <Skeleton className="h-[80px] w-full rounded-lg" />
          </div>
          <div className="flex flex-col gap-[30px] w-[628px]">
            <Skeleton className="h-[300px] w-full rounded-lg" />
            <Skeleton className="h-[300px] w-full rounded-lg" />
          </div>
        </div>
      </AppContainer>
    );
  }

  // Show error message if API call failed
  if (isError) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Texto de Apoio da página"
      >
        <div className="flex items-center justify-center h-64 text-red-400">
          Erro ao carregar os recursos do projeto. Tente novamente.
        </div>
      </AppContainer>
    );
  }

  // Show empty state if no data exists
  if (!hasData) {
    return (
      <AppContainer
        title="Gestão de Recursos"
        description="Texto de Apoio da página"
        button={
          <>
            <Button
              onClick={handleEditResources}
              variant="outline"
              size="sm"
              className="border-[#7846FF] text-[#F5F2FF] hover:bg-[#7846FF] hover:text-white"
            >
              Editar Recursos
            </Button>
            <Button
              size="sm"
              className="bg-[#7846FF] hover:bg-[#9b6fff] text-[#F5F2FF]"
            >
              <Plus size={16} />
              Criar Novo
            </Button>
          </>
        }
      >
        <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
          <FileWarning size={48} className="text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-300">Nenhum dado de recursos encontrado</h3>
          <p className="text-center max-w-md text-sm">
            Este projeto ainda não possui dados de orçamento ou recursos cadastrados.
            <br />
            Clique em "Editar Recursos" para começar a adicionar informações.
          </p>
          <Button
            onClick={handleEditResources}
            size="sm"
            className="mt-2 bg-[#7846FF] hover:bg-[#9b6fff] text-white"
          >
            Cadastrar Recursos
          </Button>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title="Gestão de Recursos"
      description="Texto de Apoio da página"
      button={
        <>
          <Button
            onClick={handleEditResources}
            variant="outline"
            size="sm"
            className="border-[#7846FF] text-[#F5F2FF] hover:bg-[#7846FF] hover:text-white"
          >
            Editar Recursos
          </Button>
          <Button
            size="sm"
            className="bg-[#7846FF] hover:bg-[#9b6fff] text-[#F5F2FF]"
          >
            <Plus size={16} />
            Criar Novo
          </Button>
        </>
      }
    >
      <div className="flex gap-4">
        {/* Left Column */}
        <div className="flex flex-col gap-4 flex-1 min-w-0">
          {/* Project Header */}
          <ProjectResourceHeader
            projectInfo={apiData.projectInfo}
            onGoToProject={handleGoToProject}
          />

          {/* Budget Summary - Expanded by default */}
          <ResourceBudgetCard
            title="Resumo do orçamento do projeto"
            defaultExpanded={true}
            budgetData={apiData.budgetSummary}
            metadata={{
              iniciadoEm: apiData.budgetSummary.iniciadoEm,
              atualizadoEm: apiData.budgetSummary.atualizadoEm,
            }}
            showEditButton={true}
            showDetails={true}
          />

          {/* Financing Plan - Collapsed by default */}
          <ResourceBudgetCard
            title="Plano de financiamento"
            defaultExpanded={false}
            totalValue={apiData.financingPlan.total}
            showDetails={false}
          />

          {/* Contracted Investments - Collapsed by default */}
          <ResourceBudgetCard
            title="Composição de investimentos e patrocínios contratados"
            defaultExpanded={false}
            totalValue={apiData.investmentsContracted.total}
            showDetails={false}
          />

          {/* Resources Composition - Collapsed by default */}
          <ResourceBudgetCard
            title="Composição de recursos, investimentos e patrocínios"
            defaultExpanded={false}
            totalValue={apiData.resourcesComposition.totalFinalProjeto}
            showDetails={false}
          />
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4 w-[400px] xl:w-[500px]">
          {/* Monthly Statistics Chart */}
          <MonthlyStatsBarChart
            data={apiData.monthlyStats}
            period="Últimos 7 dias"
            isLoading={isLoading}
          />

          {/* Resources by Law Chart */}
          <ResourcesByLawChart
            data={apiData.lawResources.data}
            total={apiData.lawResources.total}
            percentage={apiData.lawResources.percentage}
            period="Últimos 7 dias"
            isLoading={isLoading}
          />
        </div>
      </div>
    </AppContainer>
  );
}
